import { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import { Form, Formik } from "formik";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircle2,
  Clock,
  HelpCircle,
  IdCard,
  Lock,
  Mail,
  Shield,
} from "lucide-react";

import EpAwareIdVerification from "./EpAwareIdVerification";
import {
  clearEpAwareSignupArtifactsOnSkip,
  shouldAllowEpAwareVerification,
  canOfferEpAwareIdVerification,
} from "../../helpers/epAwareVerificationGuard";
import {
  getObjLength,
  getSignupUserId,
  numberToWords,
  user_signup_details,
} from "../../helpers/utils";
import { Label } from "../../common/customDesignedComponents/label";
import bbbBadge from "../../../assets/new_ui_images/bbb-badge.webp";
import { Button } from "../../common/customDesignedComponents/button";
import { Checkbox } from "../../common/customDesignedComponents/checkbox";
import soc2Badge from "../../../assets/new_ui_images/soc2-type2-badge.png";
import StatusModal from "../../common/customDesignedComponents/StatusModal";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import HubspotCookieBreakerInput from "../../common/HubspotCookieBreakerInput";
import StateDropdown from "../../common/customDesignedComponents/StateDropdown";
import oneMillionBadge from "../../../assets/new_ui_images/one-million-usd.webp";
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  SUPPORT_PHONE_FORMAT,
} from "../../helpers/constants";

const benefits = [
  "Confirm you are the rightful property owner",
  "Prevent fraudsters from enrolling your property",
  "Add an extra layer of security before activation",
  "Answers used only for verification purposes",
];

const mapQuestionOption = (option: unknown) => {
  const text = option == null || option === "" ? " " : String(option).trim();
  return {
    name: text || "-",
    value: text,
    label: text || "-",
  };
};

type ViewType = "choose" | "questions" | "id" | "mail";

type AddPropertyEpAwareContext = {
  userId: number | string;
  propertyId?: string;
  propertyAddress: Record<string, unknown>;
  partyEmail?: string;
  partyPhone?: string;
  partyName?: string;
};

interface SecurityInfoV2Props {
  initialValues?: any;
  validationSchema?: any;
  onSubmit?: (values: any, errors?: any, isChecked?: boolean) => void;
  questions?: Array<{
    question: string;
    options: string[];
    question_code?: string;
  }>;
  checkTerms?: boolean;
  setCheckTerms?: (value: boolean) => void;
  checked1?: boolean;
  setChecked1?: (value: boolean) => void;
  setPropertyListScreen?: (value: boolean) => void;
  setSkipVerificationPopup?: (value: boolean) => void;
  backToAddPropertyScreen?: () => void;
  addNewProperty?: (screen: string) => void;
  skipQuestionAnswers?: () => void;
  reduceErrorCount?: () => void;
  secureConnectionHeader?: boolean;
  isBackToAddPropertyScreen?: boolean;
  isAddPropertyFlow?: boolean;
  addPropertyIntroNote?: string;
  buildAddPropertyVerifyPayload?: () => Record<string, unknown>;
  addPropertyEpAwareContext?: AddPropertyEpAwareContext;
  onPostcardVerificationConfirm?: () => void;
  onAddPropertyIdVerified?: (response: unknown) => void;
  isThirdPartyOwnerAttestation?: boolean;
  ownershipVerificationAlreadyComplete?: boolean;
  isServiceProvider?: boolean;
  isAdminCreateUser?: boolean;
  backToSearchProperty?: (screen: string) => void;
  agreeToOwnership?: boolean;
  setAgreeToOwnership?: (value: boolean) => void;
  category?: string;
  isQuestionByPass?: boolean;
  skipVerification?: boolean;
  skipVerificationPopupOpen?: boolean;
}

const SecurityInfoV2 = ({
  initialValues = {
    houseInfo_1: "",
    houseInfo_2: "",
    houseInfo_3: "",
  },
  validationSchema,
  onSubmit,
  setPropertyListScreen,
  questions = [],
  checkTerms: checkTermsProp,
  setCheckTerms: setCheckTermsProp,
  checked1: checked1Prop,
  setChecked1: setChecked1Prop,
  setSkipVerificationPopup,
  backToAddPropertyScreen,
  addNewProperty,
  skipQuestionAnswers,
  reduceErrorCount,
  secureConnectionHeader = true,
  isBackToAddPropertyScreen = true,
  isAddPropertyFlow = false,
  addPropertyIntroNote = "",
  buildAddPropertyVerifyPayload,
  addPropertyEpAwareContext,
  onPostcardVerificationConfirm,
  onAddPropertyIdVerified,
  isThirdPartyOwnerAttestation = false,
  ownershipVerificationAlreadyComplete = false,
  isServiceProvider = false,
  isAdminCreateUser = false,
  backToSearchProperty,
  agreeToOwnership: agreeToOwnershipProp,
  setAgreeToOwnership: setAgreeToOwnershipProp,
  category = "complete_protection",
  isQuestionByPass = false,
  skipVerification = false,
  skipVerificationPopupOpen = false,
}: SecurityInfoV2Props) => {
  const dispatch = useDispatch();
  const isThirdPartyEnrollment = isAdminCreateUser || isServiceProvider;
  const [verificationMethod, setVerificationMethod] = useState<ViewType>(
    isThirdPartyEnrollment ? "questions" : "choose",
  );
  const [agreeToOwnership, setAgreeToOwnership] = useState(true); // Default to true as per original
  const [checkTerms, setCheckTerms] = useState(checkTermsProp ?? true);
  const [checked1, setChecked1] = useState(checked1Prop ?? false);
  const [verifiedCount, setVerifiedCount] = useState(1);
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const {
    propertyQuestion,
    propertyVerified,
    selectedProperty,
    propertiesList,
    createAccResponse: crerateAccData,
    getPropertyData,
    genearteQuesPayload,
    userPropertyData,
    user_id,
  } = useSelector((state: any) => state.signUpUser);

  const navigate = useNavigate();

  // Watch for propertyVerified changes and show error modal
  useEffect(() => {
    if (getObjLength(propertyVerified) && !propertyVerified?.success) {
      setOpenErrorModal(true);
    } else if (propertyVerified?.success) {
      setOpenErrorModal(false);
    }
  }, [propertyVerified]);

  useEffect(() => {
    if (!isThirdPartyEnrollment || isThirdPartyOwnerAttestation) {
      return;
    }

    if (questions.length > 0) {
      setVerificationMethod((current) =>
        current === "choose" ? "questions" : current,
      );
    }
  }, [isThirdPartyEnrollment, isThirdPartyOwnerAttestation, questions.length]);

  const epAwareAllowed = shouldAllowEpAwareVerification({
    category,
    isQuestionByPass,
    skipVerification,
    skipVerificationPopupOpen,
    verificationMethod,
    isExplicitIdChoice: verificationMethod === "id",
  });

  const canOfferEpAwareIdOption = canOfferEpAwareIdVerification({
    category,
    isQuestionByPass,
    skipVerification,
    skipVerificationPopupOpen,
  });

  useEffect(() => {
    if (verificationMethod === "id" && !epAwareAllowed) {
      setVerificationMethod(isThirdPartyEnrollment ? "questions" : "choose");
    }
  }, [epAwareAllowed, isThirdPartyEnrollment, verificationMethod]);

  const handleFormSubmit = (values: any, errors: any) => {
    if (onSubmit) {
      onSubmit(values, errors, true);
    }
  };

  const handleSkip = () => {
    const effectiveChecked1 = checked1Prop ?? checked1;
    const ownershipAgreed = agreeToOwnershipProp ?? agreeToOwnership;

    if (propertyQuestion?.data?.from_investor) {
      setPropertyListScreen?.(true);
      return;
    }

    if (isAdminCreateUser) {
      if (!effectiveChecked1) {
        setCheckTerms(false);
        if (setCheckTermsProp) {
          setCheckTermsProp(false);
        }
        return;
      }
      if (!ownershipAgreed) {
        return;
      }
      setSkipVerificationPopup?.(true);
      return;
    }

    if (effectiveChecked1) {
      setSkipVerificationPopup?.(true);
    } else {
      setCheckTerms(false);
      if (setCheckTermsProp) {
        setCheckTermsProp(false);
      }
    }
  };

  const handleTryAgain = () => {
    setVerifiedCount(verifiedCount + 1);
    dispatch(userSigupAction.clearPropertyVerified("SecurityInfoScreen"));
    setOpenErrorModal(false);
  };

  const handleFindPropertyAgain = () => {
    if (addNewProperty) {
      addNewProperty("listing_screen");
    } else {
      // Fallback: dispatch action directly
      if (propertiesList?.data?.length >= 1) {
        localStorage.setItem("button", "skip");
      }
      dispatch(userSigupAction.addNewProperty({}));
      setVerifiedCount(verifiedCount + 1);
      if (backToAddPropertyScreen) {
        backToAddPropertyScreen();
      }
    }
    setOpenErrorModal(false);
  };

  const handleSkipVerification = () => {
    clearEpAwareSignupArtifactsOnSkip();
    skipQuestionAnswers?.();
    setOpenErrorModal(false);
  };

  // Calculate remaining chances
  const remainingChances = 3 - parseInt(String(verifiedCount));
  const shouldShowTryAgainModal = verifiedCount <= 2;
  const shouldShowSkipModal = verifiedCount > 2;

  // Contact Support JSX
  const contactSupportContent = (
    <div className="text-left space-y-2">
      <p className="text-sm text-muted-foreground">
        Are you stuck here? Please contact our support team for any help.
      </p>
      <div className="space-y-1 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Email:</span>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-secondary hover:text-secondary/80 font-medium"
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">By Phone:</span>
          <a
            href={`tel:${SUPPORT_PHONE}`}
            className="text-secondary hover:text-secondary/80 font-medium"
          >
            {SUPPORT_PHONE_FORMAT}
          </a>
        </div>
      </div>
    </div>
  );

  const onChecked = (checked: boolean) => {
    setChecked1(checked);
    if (setChecked1Prop) {
      setChecked1Prop(checked);
    }
    if (!checked) {
      setCheckTerms(false);
      if (setCheckTermsProp) {
        setCheckTermsProp(false);
      }
    } else {
      setCheckTerms(true);
      if (setCheckTermsProp) {
        setCheckTermsProp(true);
      }
    }
  };

  const buildVerifyPayload = () => {
    if (buildAddPropertyVerifyPayload) {
      return buildAddPropertyVerifyPayload();
    }

    const signupDetails = user_signup_details();
    const propertyData =
      getPropertyData?.json_response || getPropertyData?.json_res || {};
    const resolvedUserId =
      crerateAccData?.data?.user_id ||
      user_id ||
      getSignupUserId() ||
      signupDetails?.user_id;
    const payload: Record<string, unknown> = {
      property_id:
        getPropertyData?.property_id || selectedProperty?.property_id,
      state_code:
        getPropertyData?.state_code || propertyData?.mm_fips_state_code,
      user_id: resolvedUserId,
      answers: { ep_aware: "verified" },
      step: 2,
      step_key: "VERIFY_VIA_EPAWARE",
      street_number: genearteQuesPayload?.street_number,
      site_route: genearteQuesPayload?.site_route,
      city: genearteQuesPayload?.city,
      state: genearteQuesPayload?.state,
      zip: genearteQuesPayload?.zip,
      country: genearteQuesPayload?.country,
      latitude: genearteQuesPayload?.latitude,
      longitude: genearteQuesPayload?.longitude,
      address: genearteQuesPayload?.address,
      from_investor: genearteQuesPayload?.from_investor,
    };

    if (
      getObjLength(userPropertyData) &&
      userPropertyData?.data?.type === "parcel"
    ) {
      return {
        ...payload,
        ...userPropertyData?.data,
      };
    }

    return payload;
  };

  const renderThirdPartyAttestation = (
    isFormValid: boolean,
    submitForm: () => void,
  ) => (
    <>
      <h1 className="text-xl md:text-2xl font-display font-medium text-foreground text-center mb-3">
        Confirm owner authorization
      </h1>
      <p className="text-muted-foreground text-center text-sm mb-6">
        The client&apos;s identity has been verified through ID validation.
        Before continuing, confirm that you have express permission from the
        lawful property owner to enroll this property on their behalf.
      </p>

      <div className="space-y-3 mb-6">
        <div
          className={`flex items-start gap-3 ${!checkTerms ? "border border-red-500 rounded p-2" : ""}`}
        >
          <Checkbox
            id="attestationAgreeToCall"
            checked={checked1Prop ?? checked1}
            onCheckedChange={(checked) => onChecked(checked as boolean)}
            className="mt-1 data-[state=checked]:bg-secondary data-[state=checked]:border-secondary"
          />
          <Label
            htmlFor="attestationAgreeToCall"
            className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
          >
            I agree that an Employee of EquityProtect may contact the client at
            any time if they find an issue or an attempt at fraud on this
            property. I understand the client will be asked challenge questions
            on that call to verify their identity. If the client receives a call
            and is not asked the challenge questions, they should hang up and
            call Customer Support directly.
          </Label>
        </div>
        {!checkTerms && (
          <p className="text-red-500 text-sm">
            Please select given terms to proceed further.
          </p>
        )}

        <div className="flex items-start gap-3">
          <Checkbox
            id="attestationAgreeToOwnership"
            checked={agreeToOwnershipProp ?? agreeToOwnership}
            onCheckedChange={(checked) => {
              const value = checked === true;
              if (setAgreeToOwnershipProp) {
                setAgreeToOwnershipProp(value);
              } else {
                setAgreeToOwnership(value);
              }
            }}
            className="mt-1 data-[state=checked]:bg-secondary data-[state=checked]:border-secondary"
          />
          <Label
            htmlFor="attestationAgreeToOwnership"
            className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
          >
            By checking this box, I confirm that I have express permission from
            the lawful property owner to add this property and authorize
            EquityProtect to verify ownership on their behalf.
          </Label>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => backToSearchProperty?.("security")}
          className="px-8 py-2.5 rounded-full font-medium text-sm w-full sm:w-auto"
        >
          BACK
        </Button>
        <Button
          type="button"
          disabled={!isFormValid}
          onClick={submitForm}
          className={`font-medium px-10 py-2.5 text-sm rounded-full shadow-md transition-all w-full sm:w-auto
            bg-secondary bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90
            hover:to-cyan-400/90 text-white hover:shadow-lg ring-2 ring-secondary/30 ring-offset-2
          `}
        >
          SUBMIT
        </Button>
      </div>
    </>
  );

  const renderMethodPicker = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-xl md:text-2xl font-display font-medium text-foreground mb-3">
          Choose how to verify ownership
        </h1>
        <p className="text-muted-foreground text-center text-sm">
          Answer secure property questions, verify with your government ID
          {onPostcardVerificationConfirm
            ? ", or request a mailed verification postcard"
            : ""}
          .
        </p>
      </div>

      <div className="grid gap-4">
        <button
          type="button"
          onClick={() => setVerificationMethod("questions")}
          className="text-left rounded-2xl border border-border p-5 hover:border-secondary hover:shadow-md transition-all bg-background"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
              <HelpCircle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-1">
                Answer challenge questions
              </h3>
              <p className="text-sm text-muted-foreground">
                Use property history questions only you should know.
              </p>
            </div>
          </div>
        </button>

        {canOfferEpAwareIdOption ? (
          <button
            type="button"
            onClick={() => {
              clearEpAwareSignupArtifactsOnSkip();
              setVerificationMethod("id");
            }}
            className="text-left rounded-2xl border border-border p-5 hover:border-secondary hover:shadow-md transition-all bg-background"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                <IdCard className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">
                  Verify with your ID
                </h3>
                <p className="text-sm text-muted-foreground">
                  Scan a QR code, complete EpAware on your phone, and we will
                  match your ID address to the property.
                </p>
              </div>
            </div>
          </button>
        ) : null}

        {onPostcardVerificationConfirm ? (
          <button
            type="button"
            onClick={() => setVerificationMethod("mail")}
            className="text-left rounded-2xl border border-border p-5 hover:border-secondary hover:shadow-md transition-all bg-background"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">
                  Mail verification postcard
                </h3>
                <p className="text-sm text-muted-foreground">
                  We will mail a postcard with a unique code to your property
                  mailing address. Enter the code after you receive it.
                </p>
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
                  <Clock className="w-3.5 h-3.5" />
                  Usually arrives in 3–5 business days
                </p>
              </div>
            </div>
          </button>
        ) : null}
      </div>
    </div>
  );

  const renderMailVerification = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-xl md:text-2xl font-display font-medium text-foreground mb-3">
          Verify by mailed postcard
        </h1>
        <p className="text-muted-foreground text-center text-sm leading-relaxed">
          After you continue, EquityProtect will mail a postcard to your
          property mailing address with a unique validation code. Enter the code
          in your account once it arrives to complete verification.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
        You can finish checkout now. Property monitoring will begin after the
        postcard code is entered.
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          type="button"
          variant="outline"
          onClick={() => setVerificationMethod("choose")}
        >
          Choose another method
        </Button>
        <Button
          type="button"
          onClick={() => onPostcardVerificationConfirm?.()}
          disabled={!onPostcardVerificationConfirm}
        >
          Continue with postcard verification
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Security Info | EquityProtect - Verify Ownership</title>
        <meta
          name="description"
          content="Verify your property ownership with security questions to protect against title fraud."
        />
      </Helmet>

      <div
        className={
          isAddPropertyFlow
            ? "bg-background"
            : "min-h-screen bg-background pb-16"
        }
      >
        <div className={isAddPropertyFlow ? "" : "container mx-auto px-4"}>
          <Formik
            enableReinitialize
            validateOnChange
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              setFieldValue,
              touched,
              setFieldTouched,
              errors,
              submitForm,
            }) => {
              const effectiveChecked1 = checked1Prop ?? checked1;
              const ownershipAgreed = agreeToOwnershipProp ?? agreeToOwnership;
              const isFormValid = isThirdPartyOwnerAttestation
                ? effectiveChecked1 && ownershipAgreed
                : (questions[0] ? values.houseInfo_1 : true) &&
                  (questions[1] ? values.houseInfo_2 : true) &&
                  (questions[2] ? values.houseInfo_3 : true) &&
                  effectiveChecked1 &&
                  ownershipAgreed;

              return (
                <Form id="signup-security-questions-form">
                  {/* Hubspot hidden input for cookie breaker */}
                  <HubspotCookieBreakerInput />

                  <div
                    className={
                      isAddPropertyFlow
                        ? "max-w-2xl mx-auto"
                        : "grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto adbox_wrapper"
                    }
                  >
                    {/* Left Column - Advertisement Box */}
                    {!isAddPropertyFlow ? (
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        className="lg:sticky lg:top-32 adbox_width"
                      >
                        <div className="bg-gradient-teal rounded-2xl p-8 md:p-10 text-white">
                          <div className="flex items-center gap-3 mb-6">
                            <Shield className="h-10 w-10 text-white" />
                            <h2 className="text-2xl md:text-3xl font-display font-medium">
                              Owner Validation
                            </h2>
                          </div>

                          <p className="text-lg text-white/90 font-medium mb-2">
                            {verificationMethod === "id"
                              ? "Verify with EpAware"
                              : "Owner Challenge Questions"}
                          </p>
                          <p className="text-white/80 text-sm mb-8 leading-relaxed">
                            {verificationMethod === "id"
                              ? "Scan the QR code with your phone to verify your government ID through EpAware. Then choose which properties in the county you want to protect."
                              : "To help confirm that you are the rightful owner, EquityProtect will ask a few secure questions based on information only you should know. This added step helps prevent fraudsters from enrolling or accessing your property and adds another layer of protection before monitoring or protection is activated. Your answers are used only for verification."}
                          </p>

                          <ul className="space-y-4 mb-8">
                            {benefits.map((benefit, index) => (
                              <motion.li
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 + index * 0.1 }}
                                className="flex items-start gap-3"
                              >
                                <CheckCircle2 className="h-5 w-5 text-white shrink-0 mt-0.5" />
                                <span>{benefit}</span>
                              </motion.li>
                            ))}
                          </ul>

                          <div className="flex items-center gap-4 pt-6 border-t border-white/20">
                            <img
                              src={oneMillionBadge}
                              alt="One Million Dollar Guarantee"
                              className="h-16 w-auto"
                            />
                            <img
                              src={bbbBadge}
                              alt="BBB Accredited Business"
                              className="h-14 w-auto"
                            />
                            <img
                              src={soc2Badge}
                              alt="SOC 2 Certified"
                              className="h-14 w-auto"
                            />
                          </div>
                        </div>
                        {/* Video Section */}
                        <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden p-6 mt-4">
                          <h3 className="text-lg font-display font-medium text-foreground mb-4 text-center">
                            Need help? Let us guide you through the process
                          </h3>
                          <div className="rounded-xl overflow-hidden">
                            <iframe
                              width="100%"
                              style={{ aspectRatio: "16/9" }}
                              src="https://www.youtube.com/embed/EsJWqu1iZH4?modestbranding=1&rel=0&showinfo=0"
                              title="EquityProtect Security Info Guide"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                            />
                          </div>
                        </div>
                      </motion.div>
                    ) : null}

                    {/* Right Column - Form */}
                    <motion.div
                      initial={{ opacity: 0, x: isAddPropertyFlow ? 0 : 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.6,
                        delay: isAddPropertyFlow ? 0 : 0.2,
                      }}
                      className={isAddPropertyFlow ? "w-full" : "adbox_width"}
                    >
                      <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
                        {/* Secure Connection Bar */}
                        {secureConnectionHeader && (
                          <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
                            <Lock className="h-4 w-4 text-white" />
                            <span className="text-white font-medium text-sm">
                              Secure connection
                            </span>
                          </div>
                        )}

                        {/* Form Content */}
                        <div className="p-6 md:p-8">
                          {isThirdPartyOwnerAttestation
                            ? renderThirdPartyAttestation(
                                isFormValid,
                                submitForm,
                              )
                            : null}

                          {!isThirdPartyOwnerAttestation &&
                            !isThirdPartyEnrollment &&
                            verificationMethod === "choose" &&
                            renderMethodPicker()}

                          {!isThirdPartyOwnerAttestation &&
                            verificationMethod === "mail" &&
                            renderMailVerification()}

                          {!isThirdPartyOwnerAttestation &&
                            verificationMethod === "id" &&
                            epAwareAllowed && (
                              <EpAwareIdVerification
                                onBack={() =>
                                  setVerificationMethod(
                                    isThirdPartyEnrollment &&
                                      Array.isArray(questions) &&
                                      questions.length > 0
                                      ? "questions"
                                      : "choose",
                                  )
                                }
                                buildVerifyPayload={buildVerifyPayload}
                                startContext={addPropertyEpAwareContext}
                                useAuthToken={Boolean(
                                  addPropertyEpAwareContext,
                                )}
                                onApplySuccess={onAddPropertyIdVerified}
                                autoStart={epAwareAllowed}
                              />
                            )}

                          {!isThirdPartyOwnerAttestation &&
                            verificationMethod === "questions" && (
                              <>
                                <div className="flex items-center justify-between gap-3 mb-4">
                                  {isThirdPartyEnrollment &&
                                  canOfferEpAwareIdOption ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Clear any stale EpAware session so
                                        // EpAwareIdVerification always starts
                                        // fresh instead of resuming an old one.
                                        clearEpAwareSignupArtifactsOnSkip();
                                        setVerificationMethod("id");
                                      }}
                                      className="text-secondary hover:text-secondary/80 font-medium text-sm transition-colors"
                                    >
                                      Verify with ID instead
                                    </button>
                                  ) : isThirdPartyEnrollment ? (
                                    <span />
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setVerificationMethod("choose")
                                      }
                                      className="text-secondary hover:text-secondary/80 font-medium text-sm transition-colors"
                                    >
                                      Change verification method
                                    </button>
                                  )}
                                </div>
                                <h1 className="text-xl md:text-2xl font-display font-medium text-foreground text-center mb-3">
                                  {isThirdPartyEnrollment
                                    ? "Confirm the client owns this property"
                                    : isAddPropertyFlow
                                      ? "Verify ownership for this property"
                                      : "Let's make sure you are the owner"}
                                </h1>
                                <p className="text-muted-foreground text-center text-sm mb-6">
                                  {isThirdPartyEnrollment
                                    ? "Answer the secure questions below on behalf of the property owner."
                                    : isAddPropertyFlow
                                      ? "Answer the secure questions below to confirm you own this property."
                                      : "Please answer the following questions about your property so we are able to identify you as the owner."}
                                </p>
                                {isAddPropertyFlow && addPropertyIntroNote ? (
                                  <p className="text-sm text-foreground/80 text-center mb-6 rounded-xl border border-secondary/20 bg-secondary/5 px-4 py-3">
                                    {addPropertyIntroNote}
                                  </p>
                                ) : null}

                                {/* House Information Section */}
                                <div className="mb-6">
                                  <h2 className="text-base font-semibold text-foreground mb-4">
                                    House information
                                  </h2>

                                  {isThirdPartyEnrollment &&
                                  questions.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-6">
                                      Loading security questions...
                                    </p>
                                  ) : null}

                                  <div className="space-y-4">
                                    {/* Question 1 */}
                                    {questions[0]?.question && (
                                      <StateDropdown
                                        stateList={
                                          questions[0]?.options?.map(
                                            mapQuestionOption,
                                          ) || []
                                        }
                                        value={values.houseInfo_1 || ""}
                                        onChange={(value) => {
                                          setFieldValue("houseInfo_1", value);
                                          // Mark field as touched and clear any existing error
                                          setFieldTouched(
                                            "houseInfo_1",
                                            true,
                                            false,
                                          );
                                          // Formik will re-validate on change; clearing the error immediately
                                          if (
                                            touched.houseInfo_1 &&
                                            errors.houseInfo_1
                                          ) {
                                            errors.houseInfo_1 = undefined;
                                          }
                                        }}
                                        name="houseInfo_1"
                                        label={questions[0].question}
                                        placeholder="Select"
                                        touched={Boolean(touched.houseInfo_1)}
                                        error={
                                          touched.houseInfo_1 &&
                                          errors.houseInfo_1
                                            ? typeof errors.houseInfo_1 ===
                                              "string"
                                              ? errors.houseInfo_1
                                              : "Please select an option to proceed further."
                                            : undefined
                                        }
                                        labelClassName="text-foreground text-sm"
                                      />
                                    )}

                                    {/* Question 2 */}
                                    {questions[1]?.question && (
                                      <StateDropdown
                                        stateList={
                                          questions[1]?.options?.map(
                                            mapQuestionOption,
                                          ) || []
                                        }
                                        value={values.houseInfo_2 || ""}
                                        onChange={(value) => {
                                          setFieldValue("houseInfo_2", value);
                                          setFieldTouched(
                                            "houseInfo_2",
                                            true,
                                            false,
                                          );
                                          if (
                                            touched.houseInfo_2 &&
                                            errors.houseInfo_2
                                          ) {
                                            errors.houseInfo_2 = undefined;
                                          }
                                        }}
                                        name="houseInfo_2"
                                        label={questions[1].question}
                                        placeholder="Select"
                                        touched={Boolean(touched.houseInfo_2)}
                                        error={
                                          touched.houseInfo_2 &&
                                          errors.houseInfo_2
                                            ? typeof errors.houseInfo_2 ===
                                              "string"
                                              ? errors.houseInfo_2
                                              : "Please select an option to proceed further."
                                            : undefined
                                        }
                                        labelClassName="text-foreground text-sm"
                                      />
                                    )}

                                    {/* Question 3 */}
                                    {questions[2]?.question && (
                                      <StateDropdown
                                        stateList={
                                          questions[2]?.options?.map(
                                            mapQuestionOption,
                                          ) || []
                                        }
                                        value={values.houseInfo_3 || ""}
                                        onChange={(value) => {
                                          setFieldValue("houseInfo_3", value);
                                          setFieldTouched(
                                            "houseInfo_3",
                                            true,
                                            false,
                                          );
                                          if (
                                            touched.houseInfo_3 &&
                                            errors.houseInfo_3
                                          ) {
                                            errors.houseInfo_3 = undefined;
                                          }
                                        }}
                                        name="houseInfo_3"
                                        label={questions[2].question}
                                        placeholder="Select"
                                        touched={Boolean(touched.houseInfo_3)}
                                        error={
                                          touched.houseInfo_3 &&
                                          errors.houseInfo_3
                                            ? typeof errors.houseInfo_3 ===
                                              "string"
                                              ? errors.houseInfo_3
                                              : "Please select an option to proceed further."
                                            : undefined
                                        }
                                        labelClassName="text-foreground text-sm"
                                      />
                                    )}
                                  </div>
                                </div>

                                {/* Notes Section */}
                                <div className="mb-6 bg-muted/30 rounded-lg p-4 border border-border">
                                  <p className="font-semibold text-foreground text-sm mb-2">
                                    Helpful Hint:
                                  </p>
                                  <ul className="list-disc ml-4 space-y-1 text-xs text-muted-foreground">
                                    <li>
                                      County Recorders record all trust, grant
                                      deeds, warranty deeds, LLC transfers as a
                                      sales/ownership change.
                                    </li>
                                    <li>
                                      The County Recorder lists the full draw of
                                      the Home Equity Line of Credit and not the
                                      amount utilized at the close of escrow.
                                    </li>
                                    <li>
                                      The original lender is the bank that
                                      funded your loan and not the current
                                      servicer.
                                    </li>
                                  </ul>
                                </div>

                                {/* Agreement Checkboxes */}
                                <div className="space-y-3 mb-6">
                                  <div
                                    className={`flex items-start gap-3 ${!checkTerms ? "border border-red-500 rounded p-2" : ""}`}
                                  >
                                    <Checkbox
                                      id="agreeToCall"
                                      checked={checked1}
                                      onCheckedChange={(checked) =>
                                        onChecked(checked as boolean)
                                      }
                                      className="mt-1 data-[state=checked]:bg-secondary data-[state=checked]:border-secondary"
                                    />
                                    <Label
                                      htmlFor="agreeToCall"
                                      className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                                    >
                                      I agree that an Employee of EquityProtect
                                      may call me at any time if they find an
                                      issue or an attempt at fraud on my
                                      property. I understand that I will be
                                      asked challenge questions on that call to
                                      verify my identity. If I receive a call
                                      and I am not asked the challenge
                                      questions, I will hang up and call
                                      Customer Support directly.
                                    </Label>
                                  </div>
                                  {!checkTerms && (
                                    <p className="text-red-500 text-sm">
                                      Please select given terms to proceed
                                      further.
                                    </p>
                                  )}

                                  <div className="flex items-start gap-3">
                                    <Checkbox
                                      id="agreeToOwnership"
                                      checked={ownershipAgreed}
                                      onCheckedChange={(checked) => {
                                        const value = checked as boolean;
                                        if (setAgreeToOwnershipProp) {
                                          setAgreeToOwnershipProp(value);
                                        } else {
                                          setAgreeToOwnership(value);
                                        }
                                      }}
                                      className="mt-1 data-[state=checked]:bg-secondary data-[state=checked]:border-secondary"
                                    />
                                    <Label
                                      htmlFor="agreeToOwnership"
                                      className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                                    >
                                      By checking this box I acknowledge that I
                                      am the owner of the property above and
                                      give EquityProtect permission to verify my
                                      ownership.
                                    </Label>
                                  </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                                  {(!isThirdPartyEnrollment ||
                                    isAdminCreateUser ||
                                    isServiceProvider) && (
                                      <Button
                                        onClick={handleSkip}
                                        variant="outline"
                                        type="button"
                                        className="px-8 py-2.5 rounded-full font-medium text-sm w-full sm:w-auto"
                                      >
                                        SKIP QUESTIONS
                                      </Button>
                                    )}
                                  <Button
                                    type="submit"
                                    disabled={!isFormValid}
                                    className={`font-medium px-10 py-2.5 text-sm rounded-full shadow-md transition-all
                                bg-secondary bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 
                                hover:to-cyan-400/90 text-white hover:shadow-lg ring-2 ring-secondary/30 ring-offset-2   
                            `}
                                  >
                                    NEXT
                                  </Button>
                                </div>

                                {/* Back Link */}
                                {isBackToAddPropertyScreen && (
                                  <div className="mt-4 text-center">
                                    <button
                                      onClick={backToAddPropertyScreen}
                                      type="button"
                                      className="text-secondary hover:text-secondary/80 font-medium text-sm transition-colors"
                                    >
                                      BACK
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>

      {/* Error Modal - Try Again (verifiedCount <= 2) */}
      {getObjLength(propertyVerified) &&
        !propertyVerified?.success &&
        shouldShowTryAgainModal && (
          <StatusModal
            open={openErrorModal}
            onOpenChange={setOpenErrorModal}
            variant="error"
            title="Verification Failed"
            message={[
              {
                content: (
                  <>
                    <p className="font-semibold text-foreground mb-2">
                      {propertyVerified?.data?.message}
                    </p>
                    <p className="text-muted-foreground">
                      You've {numberToWords(remainingChances)} more{" "}
                      {remainingChances === 1 ? "chance" : "chances"} to choose
                      correct answers or else you can Find Property again.
                    </p>
                  </>
                ),
              },
            ]}
            primaryButtonLabel={`Try Again (${remainingChances})`}
            onPrimaryAction={handleTryAgain}
            secondaryButtonLabel="Find Property again"
            onSecondaryAction={handleFindPropertyAgain}
          />
        )}

      {/* Error Modal - Skip Verification (verifiedCount > 2) */}
      {getObjLength(propertyVerified) &&
        !propertyVerified?.success &&
        shouldShowSkipModal && (
          <StatusModal
            open={openErrorModal}
            onOpenChange={(open) => {
              setOpenErrorModal(open);
              if (!open && reduceErrorCount) {
                reduceErrorCount();
              }
            }}
            variant="error"
            title="Verification Failed"
            message={[
              {
                content: (
                  <>
                    <p className="text-destructive font-semibold mb-4">
                      {propertyVerified?.data?.message}
                    </p>
                    {contactSupportContent}
                  </>
                ),
              },
            ]}
            primaryButtonLabel="Find Property again"
            onPrimaryAction={handleFindPropertyAgain}
            secondaryButtonLabel="Skip Verification"
            onSecondaryAction={handleSkipVerification}
          />
        )}
    </>
  );
};

export default SecurityInfoV2;
